import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmailEditor } from '@react-email/editor';
import '@react-email/editor/themes/default.css';
import '@react-email/editor/styles/bubble-menu.css';
import '@react-email/editor/styles/slash-command.css';
import { useMutation } from '@tanstack/react-query';
import { get, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmailFields } from './email-fields';
import { FileAttachments } from './file-attachments';
import { MessageInputActions } from './message-input-actions';
import { ModeSelector } from './mode-selector';
import { createComposerSchema, toOutgoingAttachments } from './schema';
import type { EmailEditorRef } from '@react-email/editor';
import type { FieldErrors } from 'react-hook-form';
import type { ComposerFormValues } from './schema';
import type { ComposeMode } from '../types';
import type { Id } from '@minty/shared/ids';
import {
  EDITOR_EXTENSIONS,
  composeEmailBody,
  validateEmailBody,
} from '~/components/message-composer/email-editor';
import { uploadAttachments } from '~/components/message-composer/upload-attachments';
import { useCan } from '~/hooks/use-can';
import { OUTBOUND_BODY_MAX_BYTES } from '@minty/shared/email';
import { Form } from '~/components/ui/form';
import { Separator } from '~/components/ui/separator';
import { getPostHog } from '~/lib/posthog';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

const DEFAULT_VALUES: ComposerFormValues = {
  to: [],
  cc: [],
  subject: '',
  attachments: [],
};

function MailComposerContent({
  emailId,
  mode,
  to,
  lockTo,
  header,
  onClose,
  onSent,
}: {
  emailId?: Id<'mailboxEmails'>;
  mode: ComposeMode;
  to?: string;
  lockTo?: boolean;
  // Overrides the default header (<ModeSelector/>).
  header?: React.ReactNode;
  onClose?: () => void;
  onSent?: () => void;
}) {
  const { t } = useTranslation('mailbox');
  const trpc = useTRPC();
  const sendReply = useMutation(
    trpc.mailbox.send.sendReply.mutationOptions(),
  ).mutateAsync;
  const sendNew = useMutation(
    trpc.mailbox.send.sendNew.mutationOptions(),
  ).mutateAsync;
  const generateUploadUrls = useMutation(
    trpc.mailbox.send.generateUploadUrls.mutationOptions(),
  ).mutateAsync;
  const editorRef = React.useRef<EmailEditorRef>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const schema = React.useMemo(() => createComposerSchema(t, mode), [t, mode]);
  const defaultValues = React.useMemo(
    () => ({ ...DEFAULT_VALUES, to: to ? [to] : [] }),
    [to],
  );
  const form = useForm<ComposerFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const isBusy = form.formState.isSubmitting;
  const editorPlaceholder =
    mode === 'forward' ? t('Add a message (optional)') : t('Message body');

  function handleAttach() {
    fileInputRef.current?.click();
  }

  function handleFilesSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) {
      return;
    }

    form.setValue('attachments', [...form.getValues('attachments'), ...files], {
      shouldDirty: true,
      shouldValidate: true,
    });
    event.target.value = '';
  }

  async function handleValidSubmit(values: ComposerFormValues) {
    const editor = editorRef.current?.editor;

    if (!editor) {
      notify.error(t('Unable to read editor content.'));
      return;
    }

    let body;

    try {
      body = await composeEmailBody(editor);
    } catch (error) {
      getPostHog()?.captureException(error);
      notify.error(t('Unable to read editor content.'));
      return;
    }

    const bodyResult = validateEmailBody(body, mode !== 'forward');

    if (!bodyResult.ok) {
      const { failure } = bodyResult;
      const message =
        failure.kind === 'empty'
          ? t('Add a message body.')
          : t('Message body must be {{size}} KB or smaller.', {
              size: OUTBOUND_BODY_MAX_BYTES / 1024,
            });

      form.setError('root', { message });
      return;
    }

    try {
      const attachments = await uploadFiles(values.attachments);
      const html = bodyResult.body.html;

      if (mode === 'new') {
        await sendNew({
          to: values.to,
          cc: values.cc,
          subject: values.subject.trim(),
          html,
          attachments,
        });
      } else if (emailId) {
        await sendReply({
          emailId,
          action: mode,
          html,
          ...(mode === 'forward' ? { to: values.to, cc: values.cc } : {}),
          attachments,
        });
      }
    } catch (error) {
      getPostHog()?.captureException(error);
      notify.error(t('Unable to send message.'));
      return;
    }

    notify.success(
      mode === 'forward'
        ? t('Forward queued for sending.')
        : t('Message queued for sending.'),
    );
    form.reset(defaultValues);
    editorRef.current?.editor?.commands.clearContent(true);
    onSent?.();
    onClose?.();
  }

  async function uploadFiles(files: Array<File>) {
    if (files.length === 0) {
      return [];
    }

    const uploadUrls = await generateUploadUrls({ count: files.length });
    const uploads = await uploadAttachments(files, uploadUrls);

    return toOutgoingAttachments(files, uploads);
  }

  function handleInvalidSubmit(errors: FieldErrors<ComposerFormValues>) {
    const message = ['to', 'cc', 'subject', 'attachments']
      .flatMap((field) => {
        const error = get(errors, field);

        return [
          get(error, 'message'),
          get(error, 'root.message'),
          ...(Array.isArray(error)
            ? error.map((item) => get(item, 'message'))
            : []),
        ];
      })
      .find((value): value is string => typeof value === 'string');

    const rootMessage = message ?? t('Unable to send message.');

    form.setError('root', { message: rootMessage });
  }

  return (
    <Form.Root
      form={form}
      onSubmit={handleValidSubmit}
      onInvalid={handleInvalidSubmit}
      submitMode="explicit"
      aria-busy={isBusy}
      className="flex flex-col bg-background px-4 pb-4 pt-3"
    >
      <fieldset disabled={isBusy} className="contents">
        <div className="flex flex-col gap-2 pb-2">
          {header ?? <ModeSelector mode={mode} onClose={onClose} />}
          <EmailFields mode={mode} lockTo={lockTo} />
          <Separator className="bg-border" />
        </div>

        <div className="pt-3">
          {/* The one element that grows. Past the ceiling the editor's own
              `overflow-auto` takes over, so a long draft scrolls inside the
              composer rather than pushing the feed off the screen. */}
          <div
            inert={isBusy}
            className="flex max-h-(--composer-max-height) min-h-48 overflow-hidden rounded-md border border-border bg-background"
          >
            <EmailEditor
              ref={editorRef}
              editable={!isBusy}
              className="email-composer-editor min-h-48 flex-1 overflow-auto px-4 py-3 text-sm leading-6 text-foreground outline-none"
              extensions={EDITOR_EXTENSIONS}
              placeholder={editorPlaceholder}
            />
          </div>
        </div>

        <FileAttachments />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          disabled={isBusy}
          className="hidden"
          onChange={handleFilesSelected}
        />
      </fieldset>

      <div className="mt-2.5 shrink-0">
        {form.formState.errors.root?.message && (
          <p role="alert" className="mb-2 text-sm text-destructive">
            {form.formState.errors.root.message}
          </p>
        )}
        <MessageInputActions
          mode={mode}
          onAttach={handleAttach}
          isBusy={isBusy}
        />
      </div>
    </Form.Root>
  );
}

export function MailComposer(props: Parameters<typeof MailComposerContent>[0]) {
  const allowed = useCan('reception');
  return allowed ? <MailComposerContent {...props} /> : null;
}
